import { useState, useEffect, useCallback } from 'react';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

/**
 * Hook para manejar referencias de archivos con @
 * @param {string} username - Nombre del usuario
 * @param {Object} options - Opciones del hook
 * @param {boolean} options.enabled - Si es false, no carga archivos ni detecta @ (default: true)
 * @returns {Object} - Estados y funciones para manejo de archivos
 */
const useFileReferences = (username, { enabled = true } = {}) => {
  const [availableFiles, setAvailableFiles] = useState([]);
  const [hasFileAccess, setHasFileAccess] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [referencedFiles, setReferencedFiles] = useState([]);
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [fileSearchText, setFileSearchText] = useState('');
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);

  // Cargar archivos disponibles (usa el mismo endpoint que FileManager)
  const loadAvailableFiles = useCallback(async () => {
    console.log('[loadAvailableFiles] 🔄 Iniciando carga de archivos para usuario:', username);
    if (!username) {
      console.log('[loadAvailableFiles] ❌ Sin username, abortando');
      return;
    }

    setIsLoadingFiles(true);
    try {
      // Usar el endpoint /api/files que funciona en FileManager
      const url = `${STRANDS_API_URL}/api/files?username=${encodeURIComponent(username)}`;
      console.log('[loadAvailableFiles] 📡 Llamando a:', url);
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        console.log('[loadAvailableFiles] 📦 Respuesta del servidor:', {
          success: data.success,
          fileCount: data.files?.length || 0,
          files: data.files?.map(f => f.original_name) || []
        });
        if (data.success) {
          setAvailableFiles(data.files || []);
          setHasFileAccess(true);
          console.log('[loadAvailableFiles] ✅ Archivos cargados:', data.files?.length || 0);
        } else {
          console.log('[loadAvailableFiles] ⚠️ Respuesta sin success');
          setHasFileAccess(false);
        }
      } else {
        console.log('[loadAvailableFiles] ❌ Response no OK:', response.status);
        setHasFileAccess(false);
      }
    } catch (error) {
      console.error('[useFileReferences] ❌ Error cargando archivos:', error);
      setHasFileAccess(false);
    } finally {
      setIsLoadingFiles(false);
    }
  }, [username]);

  // Cargar al montar y escuchar eventos de subida de archivos
  useEffect(() => {
    if (!enabled) return;

    if (username) {
      loadAvailableFiles();
    }

    // Escuchar evento de archivo subido desde sidebar
    const handleFileUploaded = () => {
      console.log('[useFileReferences] Evento fileUploaded recibido, recargando archivos...');
      loadAvailableFiles();
    };

    window.addEventListener('fileUploaded', handleFileUploaded);
    return () => {
      window.removeEventListener('fileUploaded', handleFileUploaded);
    };
  }, [username, loadAvailableFiles, enabled]);

  // Limpiar archivos referenciados
  const clearReferencedFiles = useCallback(() => {
    setReferencedFiles([]);
  }, []);

  // Detectar referencias @ en el texto
  const detectAtReference = useCallback((text, cursorPosition) => {
    if (!enabled) {
      setShowFileDropdown(false);
      return false;
    }

    // Validar que text sea string
    if (!text || typeof text !== 'string') {
      setShowFileDropdown(false);
      return false;
    }

    const textBeforeCursor = text.substring(0, cursorPosition);
    const atMatch = textBeforeCursor.match(/@(\S*)$/);

    if (atMatch && availableFiles.length > 0) {
      const searchText = atMatch[1]?.toLowerCase() || '';
      setFileSearchText(searchText);
      setShowFileDropdown(true);
      setSelectedFileIndex(0);
      return true;
    } else {
      setShowFileDropdown(false);
      return false;
    }
  }, [availableFiles, enabled]);

  // Actualizar archivos referenciados en el texto
  const updateReferencedFiles = useCallback((text) => {
    // Validar que text sea string
    if (!text || typeof text !== 'string') {
      console.log('[updateReferencedFiles] ❌ Texto inválido:', text);
      setReferencedFiles([]);
      return [];
    }

    // DEBUG: Mostrar información detallada
    console.log('[updateReferencedFiles] 📝 Texto recibido:', text);
    console.log('[updateReferencedFiles] 📁 Archivos disponibles:', availableFiles.length, availableFiles.map(f => f.original_name));

    const newReferenced = [];

    for (const file of availableFiles) {
      // Validar que original_name exista
      if (!file.original_name) continue;

      const escapedName = file.original_name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const fileRegex = new RegExp(`@${escapedName}(?:\\s|$|,|\\.)`, 'gi');
      const matches = fileRegex.test(text);

      // DEBUG: Mostrar cada archivo y si hace match
      if (text.includes('@')) {
        console.log(`[updateReferencedFiles] 🔍 Buscando @${file.original_name} con regex: ${fileRegex.source} -> ${matches ? '✅ MATCH' : '❌ no match'}`);
      }

      if (matches) {
        newReferenced.push(file);
      }
    }

    console.log('[updateReferencedFiles] ✅ Archivos referenciados encontrados:', newReferenced.length, newReferenced.map(f => f.original_name));
    setReferencedFiles(newReferenced);
    return newReferenced;
  }, [availableFiles]);

  // Insertar referencia de archivo en el texto
  const insertFileReference = useCallback((file, text, cursorPosition) => {
    const textBeforeCursor = text.substring(0, cursorPosition);
    const textAfterCursor = text.substring(cursorPosition);

    // Encontrar el @ para reemplazar
    const atMatch = textBeforeCursor.match(/@(\S*)$/);
    if (atMatch) {
      const beforeAt = textBeforeCursor.substring(0, atMatch.index);
      const newText = `${beforeAt}@${file.original_name} ${textAfterCursor}`;
      const newCursorPosition = beforeAt.length + file.original_name.length + 2; // +2 por @ y espacio

      setShowFileDropdown(false);
      return { newText, newCursorPosition };
    }

    return { newText: text, newCursorPosition: cursorPosition };
  }, []);

  // Obtener contenido de archivo referenciado
  const fetchFileContent = useCallback(async (fileId) => {
    console.log(`[useFileReferences] Obteniendo contenido de archivo ID: ${fileId}`);
    try {
      const url = `${STRANDS_API_URL}/api/files/${fileId}/content?username=${encodeURIComponent(username)}`;
      console.log(`[useFileReferences] URL: ${url}`);

      const response = await fetch(url);
      console.log(`[useFileReferences] Response status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        console.log(`[useFileReferences] Response data:`, {
          success: data.success,
          hasFile: !!data.file,
          file_type: data.file?.file_type,
          hasContent: !!data.file?.content_base64,
          contentLength: data.file?.content_base64?.length || 0
        });
        if (data.success) {
          return data.file;
        }
      } else {
        const errorText = await response.text();
        console.error(`[useFileReferences] Error response: ${errorText}`);
      }
      return null;
    } catch (error) {
      console.error('[useFileReferences] Error obteniendo contenido:', error);
      return null;
    }
  }, [username]);

  // Obtener contenido de todos los archivos referenciados
  // Acepta un parámetro opcional de archivos para evitar problemas de closure/timing
  const fetchAllReferencedContent = useCallback(async (filesToFetch = null) => {
    const files = filesToFetch || referencedFiles;
    console.log(`[useFileReferences] Obteniendo contenido de ${files.length} archivos referenciados`);
    const contents = [];

    for (const file of files) {
      console.log(`[useFileReferences] Procesando archivo: ${file.original_name} (ID: ${file.id})`);
      const content = await fetchFileContent(file.id);
      if (content) {
        const merged = {
          ...file,
          ...content
        };
        console.log(`[useFileReferences] ✅ Contenido obtenido para ${file.original_name}:`, {
          file_type: merged.file_type,
          hasContent: !!merged.content_base64
        });
        contents.push(merged);
      } else {
        console.warn(`[useFileReferences] ⚠️ No se pudo obtener contenido para ${file.original_name}`);
      }
    }

    console.log(`[useFileReferences] Total archivos con contenido: ${contents.length}`);
    return contents;
  }, [referencedFiles, fetchFileContent]);

  // Navegacion con teclado
  const handleKeyNavigation = useCallback((key) => {
    const filteredFiles = availableFiles.filter(f =>
      f.original_name?.toLowerCase().includes(fileSearchText.toLowerCase())
    );

    if (!showFileDropdown || filteredFiles.length === 0) return null;

    switch (key) {
      case 'ArrowUp':
        setSelectedFileIndex(prev => Math.max(0, prev - 1));
        return 'handled';
      case 'ArrowDown':
        setSelectedFileIndex(prev => Math.min(filteredFiles.length - 1, prev + 1));
        return 'handled';
      case 'Enter':
        return filteredFiles[selectedFileIndex] || null;
      case 'Escape':
        setShowFileDropdown(false);
        return 'handled';
      default:
        return null;
    }
  }, [showFileDropdown, availableFiles, fileSearchText, selectedFileIndex]);

  // Filtrar archivos para el dropdown
  const filteredFiles = availableFiles.filter(f =>
    f.original_name?.toLowerCase().includes(fileSearchText.toLowerCase())
  );

  return {
    // Estados
    availableFiles,
    filteredFiles,
    hasFileAccess,
    isLoadingFiles,
    referencedFiles,
    showFileDropdown,
    fileSearchText,
    selectedFileIndex,

    // Funciones
    loadAvailableFiles,
    detectAtReference,
    updateReferencedFiles,
    insertFileReference,
    fetchFileContent,
    fetchAllReferencedContent,
    handleKeyNavigation,
    setShowFileDropdown,
    setSelectedFileIndex,
    clearReferencedFiles
  };
};

export default useFileReferences;
